import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { renderResults } from '@theme/results-update';

/**
 * The results container: grid + pagination controls, no facet UI. Focus target
 * and scroll anchor after an ajax page swap, and the morph scope (see
 * results-update.js).
 */
const RESULTS_CONTAINER_ID = 'ResultsList';

/**
 * The URL state a page swap must NOT change — everything except `page`. Used to
 * tell a page-only back/forward (ours to handle) from a filter back/forward
 * (facets-form-component's to handle); the two handlers are complementary, so
 * exactly one of them reacts to any given popstate.
 *
 * @param {{ pathname: string, search: string }} [location] - Defaults to the live location
 * @returns {string}
 */
export function listUrlBase(location = window.location) {
  const params = new URLSearchParams(location.search);
  params.delete('page');
  params.sort();
  return `${location.pathname}?${params}`;
}

/**
 * @param {{ search: string }} [location] - Defaults to the live location
 * @returns {number} The page number the URL points at; 1 when unpaginated.
 */
export function urlPage(location = window.location) {
  return Number(new URLSearchParams(location.search).get('page') ?? 1);
}

/**
 * What a history navigation means for a paginated list.
 *
 * - `render` — same list, different page: ours to swap.
 * - `rebaseline` — the filters moved too: `facets-form-component` re-renders the
 *   whole section, we only record where we ended up. Rendering here as well would
 *   mean two renders (and two announcements) for one back-button press.
 * - `ignore` — nothing we care about changed. popstate also fires for same-URL
 *   entries (bottom-up-sheet pushes one when it opens) and for hash changes.
 *
 * @param {Object} state
 * @param {string} state.base - Non-page URL state after the navigation
 * @param {number} state.page - Page after the navigation
 * @param {string} state.renderedBase - Non-page URL state the DOM reflects
 * @param {number} state.renderedPage - Page the DOM reflects
 * @returns {'render' | 'rebaseline' | 'ignore'}
 */
export function pageChangeIntent({ base, page, renderedBase, renderedPage }) {
  if (base !== renderedBase) return 'rebaseline';
  if (page === renderedPage) return 'ignore';
  return 'render';
}

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /**
   * How this list advances between pages, from the `pagination-mode` attribute
   * (see the sections that render it). Defaults to `reload` — the safe mode that
   * needs no JS at all.
   * @returns {'infinite' | 'ajax' | 'reload'}
   */
  get paginationMode() {
    const mode = this.getAttribute('pagination-mode');
    return mode === 'infinite' || mode === 'ajax' ? mode : 'reload';
  }

  /** @type {string} The non-page URL state the current render reflects. */
  #renderedUrlBase = listUrlBase();

  /** @type {number} The page the current render reflects (ajax mode). */
  #renderedPage = urlPage();

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#prefetchNeighbours();
    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);

    if (this.paginationMode === 'ajax') {
      this.addEventListener('click', this.#handlePaginationClick);
      window.addEventListener('popstate', this.#handlePopState);
      document.addEventListener(ThemeEvents.resultsRendered, this.#handleResultsRendered);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    this.removeEventListener('click', this.#handlePaginationClick);
    window.removeEventListener('popstate', this.#handlePopState);
    document.removeEventListener(ThemeEvents.resultsRendered, this.#handleResultsRendered);
  }

  /**
   * Warm the in-memory page cache with both neighbouring pages.
   *
   * Only worth doing when a page change stays on this document: infinite scroll
   * reads the cache through `#getGridForPage`, ajax pagination hits the very same
   * section-rendering URL on click. With plain links a page change is a full
   * navigation — the cache dies with the JS context, so the two requests would be
   * pure waste (~29 KB plus two section renders server-side, per page view).
   *
   * The infinite-scroll test is the SENTINELS, not the attribute: their presence
   * is the actual proof that something reads the cache, and it holds for lists
   * that never opt into a pagination mode (e.g. `<blog-posts-list>`).
   */
  #prefetchNeighbours() {
    const hasSentinels = Boolean(this.refs.viewMoreNext || this.refs.viewMorePrevious);
    if (!hasSentinels && this.paginationMode !== 'ajax') return;

    this.#fetchPage('next');
    this.#fetchPage('previous');
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('next');

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    grid.append(...nextPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    history.pushState('', '', nextPage.url.toString());

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    history.pushState('', '', previousPage.url.toString());

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Ajax pagination: a pagination link swaps `#ResultsList` in place instead of
   * navigating. The anchors stay real links, so this is pure enhancement — without
   * JS, with a modifier key, or in `reload` mode the browser navigates as before.
   *
   * @param {MouseEvent} event
   */
  #handlePaginationClick = (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const target = event.target instanceof Element ? event.target : null;
    const link = target?.closest('a.pagination__link, a.plp-load-more-btn');
    if (!(link instanceof HTMLAnchorElement) || link.origin !== window.location.origin) return;

    event.preventDefault();
    history.pushState({}, '', link.href);
    this.#renderPage({ reveal: true });
  };

  /**
   * Back/forward between pages of the same list. Complementary to
   * `facets-form-component`'s popstate handler, which ignores `page` entirely:
   * a filter change re-renders the whole section over there, a page change is
   * ours and only touches `#ResultsList`.
   */
  #handlePopState = () => {
    const base = listUrlBase();
    const page = urlPage();
    const intent = pageChangeIntent({
      base,
      page,
      renderedBase: this.#renderedUrlBase,
      renderedPage: this.#renderedPage,
    });

    if (intent === 'ignore') return;

    this.#renderedUrlBase = base;
    this.#renderedPage = page;

    // No reveal: the browser restores the scroll position for history entries.
    if (intent === 'render') this.#renderPage({ reveal: false });
  };

  /**
   * Renders the page the live URL points at (so callers push history first).
   *
   * The render URL is derived from `location` exactly the way `#getPage` derives
   * the prefetch URLs — same params, same explicit `page`, no hash — so the
   * section-rendering cache key matches and a click on a neighbouring page needs
   * no request at all.
   *
   * @param {{ reveal: boolean }} options - Whether to pull the viewport and focus to the new list
   */
  async #renderPage({ reveal }) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(urlPage()));
    url.hash = '';

    this.#renderedUrlBase = listUrlBase();
    this.#renderedPage = urlPage();

    document.getElementById(RESULTS_CONTAINER_ID)?.setAttribute('aria-busy', 'true');

    try {
      await renderResults({ sectionId: this.sectionId, reason: 'page', url, scope: 'results' });
      if (reveal) this.#revealResults();
    } catch (error) {
      console.error('[paginated-list] page render failed', { url: url.toString(), error });
    } finally {
      document.getElementById(RESULTS_CONTAINER_ID)?.removeAttribute('aria-busy');
    }
  }

  /**
   * After a page swap: bring the top of the list into view and move focus there.
   *
   * `instant`, not smooth — a smooth scroll would race the view transition and
   * read as sluggish. The offset for the sticky header comes from
   * `scroll-margin-top` in CSS rather than arithmetic here.
   */
  #revealResults() {
    const container = document.getElementById(RESULTS_CONTAINER_ID);
    if (!container) return;

    // Only pull the viewport up when the new list starts above it — if its top is
    // already visible, moving the page would be gratuitous.
    if (container.getBoundingClientRect().top < 0) {
      container.scrollIntoView({ behavior: 'instant', block: 'start' });
    }

    container.focus({ preventScroll: true });
  }

  /**
   * Any results render (ours or a facet change) invalidates our idea of where we
   * are. The element survives the morph, so `connectedCallback` does not run
   * again — the URL baseline and the neighbour prefetch have to be re-armed here.
   *
   * @param {Event} event
   */
  #handleResultsRendered = (event) => {
    const detail = /** @type {{ sectionId?: string }} */ (/** @type {CustomEvent} */ (event).detail);
    if (detail?.sectionId !== this.sectionId) return;

    this.#renderedUrlBase = listUrlBase();
    this.#renderedPage = urlPage();

    requestIdleCallback(() => this.#prefetchNeighbours());
  };

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Re-warm the cache for the freshly filtered list (no-op in reload mode)
        this.#prefetchNeighbours();
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
